/**
 * Platform: uitnodigingen over alle organisaties (superadmin)
 */
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

function statusLabel(row: {
  accepted_at: string | null;
  revoked_at: string | null;
  expires_at: string;
}): { label: string; className: string } {
  if (row.accepted_at) return { label: 'Geaccepteerd', className: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100' };
  if (row.revoked_at) return { label: 'Ingetrokken', className: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-100' };
  const exp = Date.parse(row.expires_at);
  if (Number.isFinite(exp) && exp < Date.now()) {
    return { label: 'Verlopen', className: 'bg-amber-100 text-amber-950 dark:bg-amber-950/40 dark:text-amber-50' };
  }
  return { label: 'Open', className: 'bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-100' };
}

export function AdminUitnodigingen() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-invitations'],
    queryFn: () => api.listAdminInvitations(),
    enabled: isSuper,
  });

  if (!isSuper) {
    return <Navigate to="/admin" replace />;
  }

  const rows = data?.invitations ?? [];
  const errMsg = error instanceof Error ? error.message : String(error ?? '');

  return (
    <div>
      <SeoMeta
        path="/admin/uitnodigingen"
        noindex
        title="Uitnodigingen"
        description="Platformbeheer: overzicht van teamuitnodigingen bij klanten."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Uitnodigingen' }]} />
      <h1 className="mb-2 text-2xl font-bold text-text">Uitnodigingen</h1>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        Overzicht van uitnodigingen die accountbeheerders naar collega’s sturen. Handig om te zien wanneer teams worden
        uitgebreid (na een gekozen pakket).
      </p>

      {isLoading && <p className="text-sm text-text-muted">Laden…</p>}
      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100" role="alert">
          {errMsg}
        </p>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
          <Mail className="mb-3 h-10 w-10 text-text-muted" aria-hidden />
          <p className="text-sm text-text-muted">Nog geen uitnodigingen geregistreerd.</p>
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface-card shadow-sm">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface text-xs font-semibold uppercase tracking-wide text-text-muted">
                <th className="p-3">Datum</th>
                <th className="p-3">Organisatie</th>
                <th className="p-3">Uitgenodigd</th>
                <th className="p-3">Door</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = statusLabel(r);
                const d = new Date(r.created_at);
                const dateStr = Number.isFinite(d.getTime()) ? d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : r.created_at;
                return (
                  <tr key={r.invitation_id} className="border-t border-border">
                    <td className="whitespace-nowrap p-3 text-text-muted">{dateStr}</td>
                    <td className="p-3">
                      <div className="font-medium text-text">{r.tenant_name}</div>
                      <div className="font-mono text-[11px] text-text-muted">{r.tenant_id}</div>
                    </td>
                    <td className="p-3 font-mono text-xs">{r.email}</td>
                    <td className="p-3 text-text-muted">{r.invited_by_email ?? '—'}</td>
                    <td className="p-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${st.className}`}>
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
